<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p class="mt-1 text-sm text-gray-600">
            Welcome back! New features and insights are available.
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="refreshDashboard"
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            :disabled="loading"
          >
            <i
              class="material-icons text-sm mr-2"
              :class="{ 'animate-spin': loading }"
            >
              refresh
            </i>
            Refresh
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <MetricCard
        label="Total Tenants"
        :value="metrics.totalTenants || 0"
        icon="business"
        icon-color="blue"
        :additional-info="getTotalTenantsInfo()"
        :percentage="100"
        :status="'info'"
      />
      <MetricCard
        label="Active Tenants"
        :value="metrics.activeTenants || 0"
        icon="verified"
        icon-color="green"
        :additional-info="getActiveTenantsInfo()"
        :percentage="getActivePercentage()"
        :status="getActiveStatus()"
      />
      <MetricCard
        label="Non-Renewing Tenants"
        :value="metrics.nonRenewingTenants || 0"
        icon="warning"
        icon-color="orange"
        :additional-info="getNonRenewingInfo()"
        :percentage="getNonRenewingPercentage()"
        :status="getNonRenewingStatus()"
      />
      <MetricCard
        label="Expired Tenants"
        :value="metrics.expiredTenants || 0"
        icon="schedule"
        icon-color="red"
        :additional-info="getExpiredTenantsInfo()"
        :percentage="getExpiredPercentage()"
        :status="getExpiredStatus()"
      />
    </div>
    <div class="mb-8">
      <PlanAnalyticsWidget
        :plan-data="tenantByPlans"
        :total-tenants="metrics.activeTenants"
      />
    </div>

    <div class="mb-8">
      <div v-if="loading" class="text-center py-8">
        <i class="material-icons text-gray-400 text-3xl animate-spin">
          refresh
        </i>
        <p class="text-gray-500 mt-2">Loading frequency insights...</p>
      </div>
      <FrequencyInsightsWidget
        v-else-if="
          tenantByPlans.frequency_stats &&
            Object.keys(tenantByPlans.frequency_stats).length > 0
        "
        :frequency-data="tenantByPlans.frequency_stats"
        @view-details="viewFrequencyDetails"
      />
      <div
        v-else
        class="bg-white rounded-lg shadow-md border border-gray-200 p-8 text-center"
      >
        <i class="material-icons text-gray-400 text-4xl mb-2">
          timeline
        </i>
        <p class="text-gray-500">No frequency data available</p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-8">
      <GeographicalWidget
        :geographical-data="geographicalData"
        :loading="loading"
        @refresh="fetchGeographicalData"
        @view-details="viewGeographicalDetails"
      />
    </div>

    <!-- Recently Registered Tenants Section -->
    <div class="mb-8">
      <RecentlyRegisteredTenantsWidget
        :recent-tenants="recentlyRegisteredTenants.data || []"
        :loading="tenantRegistrationLoading"
        :total-count="recentlyRegisteredTenants.count || 0"
        :current-filter="recentlyRegisteredTenants.filter || 'this_week'"
        :date-range="recentlyRegisteredTenants.date_range || null"
        @period-changed="onTenantPeriodChanged"
        @refresh="fetchRecentlyRegisteredTenants"
      />
    </div>

    <FrequencyDetailsModal
      :show="showFrequencyModal"
      :frequency-details="frequencyDetails"
      :flexibility-analysis="flexibilityAnalysis"
      :commitment-analysis="commitmentAnalysis"
      :business-impact="businessImpact"
      :recommendations="recommendations"
      :total-tenants="totalTenants"
      :active-frequency-types="activeFrequencyTypes"
      :dominant-frequency="dominantFrequency"
      :diversity-score="diversityScore"
      @close="closeFrequencyModal"
      @export-completed="onExportCompleted"
    />

    <GeographicalDetailsModal
      :show="showGeographicalDetailsModal"
      :geographical-data="geographicalData"
      @close="closeGeographicalDetailsModal"
      @export-completed="onExportCompleted"
    />
  </div>
</template>

<script>
import axios from "axios";
import MetricCard from "@/components/Dashboard/MetricCard.vue";
import FrequencyInsightsWidget from "@/components/Dashboard/FrequencyInsightsWidget.vue";
import FrequencyDetailsModal from "@/components/Dashboard/FrequencyDetailsModal.vue";
import GeographicalWidget from "@/components/Dashboard/GeographicalWidget.vue";
import GeographicalDetailsModal from "@/components/Dashboard/GeographicalDetailsModal.vue";
import PlanAnalyticsWidget from "@/components/Dashboard/PlanAnalyticsWidget.vue";
import RecentlyRegisteredTenantsWidget from "@/components/Dashboard/RecentlyRegisteredTenantsWidget.vue";

export default {
  components: {
    MetricCard,
    FrequencyInsightsWidget,
    FrequencyDetailsModal,
    GeographicalWidget,
    GeographicalDetailsModal,
    PlanAnalyticsWidget,
    RecentlyRegisteredTenantsWidget
  },
  data() {
    return {
      loading: false,
      selectedTimeRange: "30d",
      tenantByPlans: {
        data: [],
        frequency_stats: {},
        plan_stats: {}
      },
      metrics: {
        totalUsers: 0,
        activeTenants: 0,
        trialTenants: 0,
        expiredTenants: 0,
        nonRenewingTenants: 0,
        inactiveTenants: 0,
        monthlyRevenue: 0,
        successRate: 0,
        systemHealth: {
          apiResponseTime: 0,
          uptime: 0,
          errorRate: 0
        },
        quickStats: {
          activeTenants: 0,
          expiredTenants: 0,
          trialTenants: 0,
          nonRenuningTenants: 0
        }
      },
      userGrowthData: [],
      revenueDistributionData: [],
      chartCategories: [],
      geographicalData: [],
      recentTenants: [],
      topPlans: [],
      recentActivities: [],
      tenantColumns: [
        { key: "name", label: "Tenant Name", type: "text" },
        { key: "plan", label: "Plan", type: "badge" },
        { key: "users", label: "Users", type: "text" },
        { key: "created_at", label: "Created", type: "date" }
      ],
      planColumns: [
        { key: "name", label: "Plan Name", type: "text" },
        { key: "subscribers", label: "Subscribers", type: "text" },
        { key: "revenue", label: "Revenue", type: "currency" },
        { key: "growth", label: "Growth", type: "progress" }
      ],
      showFrequencyModal: false,
      frequencyDetails: [],
      flexibilityAnalysis: "",
      commitmentAnalysis: "",
      businessImpact: "",
      recommendations: "",
      totalTenants: 0,
      activeFrequencyTypes: 0,
      dominantFrequency: "",
      diversityScore: 0,
      showGeographicalDetailsModal: false,
      recentlyRegisteredTenants: {
        data: [],
        filter: "this_week",
        count: 0,
        date_range: null
      },
      tenantRegistrationLoading: false,
      planDistributionChartData: [],
      totalActiveTenants: 0,
      activePlanTypes: 0,
      mostPopularPlanName: ""
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadDashboardData();
    });
  },
  methods: {
    async clearCacheData() {
      try {
        await axios.get("dashboard/refresh-cache");
      } catch (error) {
        // Cache clear failed, but continue with refresh
      }
    },
    async fetchRecentlyRegisteredTenants(period = null) {
      this.tenantRegistrationLoading = true;

      try {
        const selectedPeriod =
          period || this.recentlyRegisteredTenants.filter || "this_week";

        const response = await axios.get(
          "dashboard/recently-registered-tenants",
          {
            params: {
              period: selectedPeriod
            }
          }
        );

        // Update the data structure with API response
        this.recentlyRegisteredTenants = {
          data: response.data.data || [],
          filter: response.data.filter || selectedPeriod,
          count: response.data.count || 0,
          date_range: response.data.date_range || null
        };
      } catch (error) {
        // Reset to empty state on error
        this.recentlyRegisteredTenants = {
          data: [],
          filter: period || "this_week",
          count: 0,
          date_range: null
        };
      } finally {
        this.tenantRegistrationLoading = false;
      }
    },
    async fetchDashBoardMetrics() {
      try {
        const response = await axios.get("dashboard/metrics");
        this.metrics = { ...this.metrics, ...response.data.data };
      } catch (error) {
        this.generateMockMetrics();
      }
    },
    async fetchGeographicalData() {
      try {
        const response = await axios.get("/dashboard/geographical");
        this.geographicalData = response.data.data;
      } catch (error) {
        // Handle error silently
      }
    },
    async fetchTenantByPlans() {
      try {
        const response = await axios.get("dashboard/tenant-by-plan");
        const cleanData = JSON.parse(JSON.stringify(response.data));

        this.tenantByPlans = {
          data: cleanData.data || [],
          frequency_stats: cleanData.frequency_stats || {},
          plan_stats: cleanData.plan_stats || {}
        };

        this.updateMetricsFromPlanData();
      } catch (error) {
        this.generateMockPlanData();
      }
    },
    updateMetricsFromPlanData() {
      if (this.tenantByPlans.frequency_stats) {
        this.metrics.quickStats = {
          activeTenants:
            this.tenantByPlans.frequency_stats.total_active_tenants || 0,
          expiredTenants: this.metrics.quickStats.expiredTenants || 0,
          trialTenants:
            this.tenantByPlans.frequency_stats.monthly_active_tenants || 0,
          nonRenuningTenants: this.metrics.quickStats.nonRenuningTenants || 0
        };

        this.metrics.activeTenants =
          this.tenantByPlans.frequency_stats.total_active_tenants || 0;
        this.metrics.trialTenants =
          this.tenantByPlans.frequency_stats.monthly_active_tenants || 0;
      }
    },

    generateMockMetrics() {
      this.metrics = {
        ...this.metrics,
        totalUsers: 15234,
        activeTenants: 4,
        trialTenants: 3,
        expiredTenants: 2,
        nonRenewingTenants: 1,
        systemHealth: {
          apiResponseTime: 145,
          uptime: 99.9,
          errorRate: 0.1
        }
      };
    },

    generateMockPlanData() {
      this.tenantByPlans = {
        data: [
          { name: "Starter Plan", subscribers: 3 },
          { name: "Premium Plan", subscribers: 1 }
        ],
        frequency_stats: {
          monthly_active_tenants: 3,
          yearly_active_tenants: 0,
          triennial_active_tenants: 1,
          other_frequency_tenants: 0,
          total_active_tenants: 4
        },
        plan_stats: {
          starter: 3,
          basic: 0,
          standard: 0,
          professional: 0,
          premium: 1,
          old_basic: 0,
          old_premium: 0,
          other: 0
        }
      };
    },

    async loadDashboardData() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchDashBoardMetrics(),
          this.fetchTenantByPlans(),
          this.fetchGeographicalData(),
          this.fetchRecentlyRegisteredTenants()
        ]);
      } catch (error) {
        // Handle error silently
      } finally {
        this.loading = false;
      }
    },
    refreshDashboard() {
      this.clearCacheData();
      this.loadDashboardData();
    },
    viewFrequencyDetails(details) {
      this.frequencyDetails = details.frequencyDetails || [];
      this.flexibilityAnalysis = details.flexibilityAnalysis || "";
      this.commitmentAnalysis = details.commitmentAnalysis || "";
      this.businessImpact = details.businessImpact || "";
      this.recommendations = details.recommendations || "";
      this.totalTenants = details.totalTenants || 0;
      this.activeFrequencyTypes = details.activeFrequencyTypes || 0;
      this.dominantFrequency = details.dominantFrequency || "";
      this.diversityScore = details.diversityScore || 0;

      this.showFrequencyModal = true;
    },

    closeFrequencyModal() {
      this.showFrequencyModal = false;
    },

    onExportCompleted() {
      // Export completed
    },

    viewGeographicalDetails(details) {
      if (details && details.geographicalData) {
        this.geographicalData = details.geographicalData || [];
      }

      this.showGeographicalDetailsModal = true;
    },

    closeGeographicalDetailsModal() {
      this.showGeographicalDetailsModal = false;
    },

    onTenantPeriodChanged(period) {
      this.fetchRecentlyRegisteredTenants(period);
    },

    getTotalTenantsInfo() {
      const total = this.metrics.totalTenants || 0;
      const breakdown = [
        this.metrics.activeTenants || 0,
        this.metrics.nonRenewingTenants || 0,
        this.metrics.expiredTenants || 0,
        this.metrics.trialTenants || 0
      ];
      const calculated = breakdown.reduce((sum, val) => sum + val, 0);

      return {
        subtitle: `${calculated} of ${total} accounted`,
        description: "Total tenant count across all statuses",
        healthScore: total > 0 ? Math.min(100, (calculated / total) * 100) : 0
      };
    },

    getActiveTenantsInfo() {
      const active = this.metrics.activeTenants || 0;
      const total = this.metrics.totalTenants || 0;
      const percentage =
        total > 0 ? Math.round((active / total) * 100 * 10) / 10 : 0;

      return {
        subtitle: `${percentage}% of total tenants`,
        description:
          active > 0 ? "Currently active and paying" : "No active tenants",
        healthScore: percentage
      };
    },

    getActivePercentage() {
      const active = this.metrics.activeTenants || 0;
      const total = this.metrics.totalTenants || 0;
      return total > 0 ? Math.round((active / total) * 100 * 10) / 10 : 0;
    },

    getActiveStatus() {
      const percentage = this.getActivePercentage();
      if (percentage >= 70) return "excellent";
      if (percentage >= 50) return "good";
      if (percentage >= 30) return "warning";
      return "critical";
    },

    getNonRenewingInfo() {
      const nonRenewing = this.metrics.nonRenewingTenants || 0;
      const total = this.metrics.totalTenants || 0;
      const percentage =
        total > 0 ? Math.round((nonRenewing / total) * 100 * 10) / 10 : 0;

      return {
        subtitle: `${percentage}% of total tenants`,
        description:
          nonRenewing > 0
            ? "Require attention for renewal"
            : "All tenants renewing",
        healthScore: Math.max(0, 100 - percentage * 10) // Adjusted multiplier for health score
      };
    },

    getNonRenewingPercentage() {
      const nonRenewing = this.metrics.nonRenewingTenants || 0;
      const total = this.metrics.totalTenants || 0;
      return total > 0 ? Math.round((nonRenewing / total) * 100 * 10) / 10 : 0;
    },

    getNonRenewingStatus() {
      const percentage = this.getNonRenewingPercentage();
      if (percentage === 0) return "excellent";
      if (percentage <= 10) return "good";
      if (percentage <= 25) return "warning";
      return "critical";
    },

    getExpiredTenantsInfo() {
      const expired = this.metrics.expiredTenants || 0;
      const total = this.metrics.totalTenants || 0;
      const percentage =
        total > 0 ? Math.round((expired / total) * 100 * 10) / 10 : 0;

      return {
        subtitle: `${percentage}% of total tenants`,
        description:
          expired > 0 ? "Subscriptions have ended" : "No expired tenants",
        healthScore: Math.max(0, 100 - percentage * 5) // Adjusted multiplier for health score
      };
    },

    getExpiredPercentage() {
      const expired = this.metrics.expiredTenants || 0;
      const total = this.metrics.totalTenants || 0;
      return total > 0 ? Math.round((expired / total) * 100 * 10) / 10 : 0;
    },

    getExpiredStatus() {
      const percentage = this.getExpiredPercentage();
      if (percentage === 0) return "excellent";
      if (percentage <= 15) return "good";
      if (percentage <= 30) return "warning";
      return "critical";
    }
  }
};
</script>
